import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiDroplet, FiHome, FiZap, FiSettings, 
  FiCheckCircle, FiArrowRight, FiInfo 
} from 'react-icons/fi';
import { FaSolarPanel, FaBatteryFull, FaLightbulb } from 'react-icons/fa';
import { useModal } from '../../context/ModalContext';

const Products = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const { openModal } = useModal();

  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'pumps', name: 'Solar Pumps' },
    { id: 'rooftop', name: 'Rooftop Systems' },
    { id: 'panels', name: 'Solar Panels' },
    { id: 'accessories', name: 'Accessories' },
    { id: 'water', name: 'Arohix Water' },
  ];

  const products = [
    {
      id: 1,
      category: 'pumps',
      name: 'Solar Agriculture Pump - 3HP',
      description: 'High-efficiency submersible pump for agricultural irrigation. Ideal for small to medium farms.',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=500',
      icon: <FiDroplet className="text-xl" />,
      features: ['3 HP Motor', 'DC Submersible', '30m Head', '5-Year Warranty'],
      price: 'Under PM KUSUM',
    },
    {
      id: 2,
      category: 'pumps',
      name: 'Solar Agriculture Pump - 5HP',
      description: 'Powerful solar pump for large-scale agricultural needs with advanced VFD technology.',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=500',
      icon: <FiDroplet className="text-xl" />,
      features: ['5 HP Motor', 'AC/DC Compatible', '50m Head', '5-Year Warranty'],
      price: 'Under PM KUSUM',
    },
    {
      id: 3,
      category: 'pumps',
      name: 'Solar Agriculture Pump - 7.5HP',
      description: 'Industrial-grade solar pump for extensive irrigation requirements.',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=500',
      icon: <FiDroplet className="text-xl" />,
      features: ['7.5 HP Motor', 'Heavy Duty', '70m Head', '5-Year Warranty'],
      price: 'Under PM KUSUM',
    },
    {
      id: 4,
      category: 'pumps',
      name: 'Solar Surface Pump',
      description: 'Surface-mounted solar pump for wells and open water sources.',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=500',
      icon: <FiDroplet className="text-xl" />,
      features: ['Monoblock Design', 'Easy Installation', 'Self Priming', '3-Year Warranty'],
      price: 'Contact for Price',
    },
    {
      id: 5,
      category: 'rooftop',
      name: 'Residential Solar Rooftop - 3kW',
      description: 'Complete on-grid solar solution for homes with net metering support.',
      image: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=500',
      icon: <FiHome className="text-xl" />,
      features: ['3kW Capacity', 'On-Grid System', 'Net Metering', '25-Year Panel Warranty'],
      price: '₹1,80,000 onwards',
    },
    {
      id: 6,
      category: 'rooftop',
      name: 'Residential Solar Rooftop - 5kW',
      description: 'Medium capacity rooftop system ideal for larger homes and small businesses.',
      image: 'https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?w=500',
      icon: <FiHome className="text-xl" />,
      features: ['5kW Capacity', 'On-Grid System', 'Net Metering', '25-Year Panel Warranty'],
      price: '₹2,80,000 onwards',
    },
    {
      id: 7,
      category: 'rooftop',
      name: 'Commercial Solar Rooftop - 10kW+',
      description: 'Scalable commercial rooftop solutions for businesses and industries.',
      image: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=500',
      icon: <FiHome className="text-xl" />,
      features: ['10kW+ Capacity', 'Custom Design', 'OPEX/CAPEX Models', '25-Year Panel Warranty'],
      price: 'Custom Quote',
    },
    {
      id: 8,
      category: 'panels',
      name: 'Monocrystalline Solar Panel - 545W',
      description: 'Premium high-efficiency monocrystalline panels for maximum power output.',
      image: 'https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?w=500',
      icon: <FaSolarPanel className="text-xl" />,
      features: ['545W Output', '21% Efficiency', 'Half-Cut Cells', '25-Year Warranty'],
      price: '₹15,000 per panel',
    },
    {
      id: 9,
      category: 'panels',
      name: 'Polycrystalline Solar Panel - 335W',
      description: 'Cost-effective polycrystalline panels with reliable performance.',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=500',
      icon: <FaSolarPanel className="text-xl" />,
      features: ['335W Output', '17% Efficiency', 'ALMM Listed', '25-Year Warranty'],
      price: '₹9,500 per panel',
    },
    {
      id: 10,
      category: 'panels',
      name: 'Bifacial Solar Panel - 550W',
      description: 'Dual-sided power generation for up to 30% more energy yield.',
      image: 'https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?w=500',
      icon: <FaSolarPanel className="text-xl" />,
      features: ['550W Output', 'Bifacial Design', 'Glass-Glass', '30-Year Warranty'],
      price: '₹18,000 per panel',
    },
    {
      id: 11,
      category: 'accessories',
      name: 'Solar Inverter - 5kW',
      description: 'High-efficiency grid-tied inverter with MPPT technology.',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=500',
      icon: <FiZap className="text-xl" />,
      features: ['5kW Capacity', 'MPPT Technology', 'WiFi Monitoring', '5-Year Warranty'],
      price: '₹45,000',
    },
    {
      id: 12,
      category: 'accessories',
      name: 'Solar Pump Controller',
      description: 'Advanced VFD controller for solar water pump systems.',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=500',
      icon: <FiSettings className="text-xl" />,
      features: ['MPPT Based', 'Dry Run Protection', 'LCD Display', '2-Year Warranty'],
      price: '₹25,000 onwards',
    },
    {
      id: 13,
      category: 'accessories',
      name: 'Solar Battery - 150Ah',
      description: 'Deep cycle tubular battery for off-grid and backup systems.',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=500',
      icon: <FaBatteryFull className="text-xl" />,
      features: ['150Ah Capacity', 'Tubular Technology', 'Deep Cycle', '5-Year Warranty'],
      price: '₹18,000',
    },
    {
      id: 14,
      category: 'accessories',
      name: 'Solar Street Light - 30W',
      description: 'All-in-one solar street light for roads, parks, and campuses.',
      image: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=500',
      icon: <FaLightbulb className="text-xl" />,
      features: ['30W LED', 'Motion Sensor', 'Dusk to Dawn', '3-Year Warranty'],
      price: '₹12,000',
    },
    {
      id: 15,
      category: 'water',
      name: 'Arohix Packaged Drinking Water - 1 Litre',
      description: 'A product of our subsidiary company Arohix. Packaged drinking water enriched with active minerals — pure, refreshing, and safe for everyday use.',  
      image: '/products/aerohix-water-1l.jpeg',
      icon: <FiDroplet className="text-xl" />,
      features: ['1 Litre Bottle', 'Active Minerals', 'BIS Certified', 'ISI Marked'],
      price: 'Contact for Price',
      externalLink: 'https://arohix.in/',
    },
    {
      id: 16,
      category: 'water',
      name: 'Arohix Packaged Drinking Water - 500ml',
      description: 'A product of our subsidiary company Arohix. Compact 500ml bottle enriched with active minerals, perfect for on-the-go hydration.',  
      image: '/products/aerohix-water-500ml.png',
      icon: <FiDroplet className="text-xl" />,
      features: ['500ml Bottle', 'Active Minerals', 'BIS Certified', 'Portable Size'],
      price: 'Contact for Price',
      externalLink: 'https://arohix.in/',
    },
    {
      id: 17,
      category: 'water',
      name: 'Arohix Packaged Drinking Water - 200ml',
      description: 'A product of our subsidiary company Arohix. Convenient 200ml single-serve bottle, ideal for events, hospitality, and travel.',  
      image: '/products/aerohix-water-200ml.png',
      icon: <FiDroplet className="text-xl" />,
      features: ['200ml Bottle', 'Active Minerals', 'BIS Certified', 'Single Serve'],
      price: 'Contact for Price',
      externalLink: 'https://arohix.in/',
    },
  ];

  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[280px] md:h-[340px] flex items-center justify-center overflow-hidden mt-20 md:mt-24">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920')] bg-cover bg-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-dark-900/90 via-dark-800/80 to-dark-900/70" />
        <div className="container relative z-10 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl md:text-5xl font-extrabold mb-4 uppercase tracking-wide">Our Products</h1>
            <p className="text-base md:text-xl text-white/90 mb-4">Comprehensive range of high-quality solar products for every need</p>
            <div className="flex items-center justify-center gap-2 text-sm text-white/80">
              <Link to="/" className="hover:text-solar-400 transition-colors">Home</Link>
              <span>/</span>
              <span className="text-solar-400">Products</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-6 md:py-8 bg-gray-100 sticky top-[72px] md:top-[80px] z-40">
        <div className="container">
          <div className="flex flex-wrap justify-center gap-2 md:gap-3">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`px-4 md:px-6 py-2 md:py-3 font-heading font-semibold text-xs md:text-sm uppercase tracking-wide transition-all rounded-lg ${
                  activeCategory === cat.id 
                    ? 'bg-gradient-to-r from-solar-500 to-solar-600 text-white shadow-lg' 
                    : 'bg-white text-dark-600 hover:text-primary-500 shadow'
                }`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 md:py-24 bg-white">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                className="bg-white shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col md:flex-row overflow-hidden group rounded-2xl"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <div className="relative md:w-2/5 h-60 md:h-auto overflow-hidden">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute top-4 left-4 bg-gradient-to-r from-primary-500 to-solar-500 text-white px-3 py-1 text-xs font-semibold rounded-md">
                    {product.price}
                  </div>
                  <div className="absolute bottom-4 right-4 w-12 h-12 bg-white/90 text-primary-500 flex items-center justify-center shadow-lg rounded-lg">
                    {product.icon}
                  </div>
                </div>
                <div className="md:w-3/5 p-6">
                  <h3 className="text-xl font-bold text-dark-800 mb-2">{product.name}</h3>
                  <p className="text-sm text-dark-500 mb-4 leading-relaxed">{product.description}</p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {product.features.map((feature, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-dark-600 px-3 py-1 rounded-md">
                        <FiCheckCircle className="text-secondary-500" /> {feature}
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={openModal} className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-5 py-2 text-sm font-semibold hover:from-primary-600 hover:to-primary-700 transition-all rounded-lg">
                      Get Quote <FiArrowRight />
                    </button>
                    {product.externalLink ? (
                      <a href={product.externalLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border-2 border-dark-300 text-dark-600 px-5 py-2 text-sm font-semibold hover:border-primary-500 hover:text-primary-500 transition-all rounded-lg">
                        <FiInfo /> Details
                      </a>
                    ) : (
                      <button className="inline-flex items-center gap-2 border-2 border-dark-300 text-dark-600 px-5 py-2 text-sm font-semibold hover:border-primary-500 hover:text-primary-500 transition-all rounded-lg">
                        <FiInfo /> Details
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PM KUSUM Info */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">Government Scheme</span>
              <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4 md:mb-6 leading-tight">PM KUSUM Scheme</h2>
              <p className="text-dark-500 leading-relaxed mb-6">
                The Pradhan Mantri Kisan Urja Suraksha Evam Utthan Mahabhiyan (PM KUSUM) scheme 
                aims to provide subsidized solar pumps to farmers across India, reducing 
                dependency on grid electricity and diesel pumps.
              </p>
              <h4 className="text-lg font-bold text-dark-800 mb-4">Benefits of PM KUSUM:</h4>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-dark-600"><FiCheckCircle className="text-secondary-500 text-xl" /> Up to 90% subsidy on solar pumps</li>
                <li className="flex items-center gap-3 text-dark-600"><FiCheckCircle className="text-secondary-500 text-xl" /> Free electricity for irrigation</li>
                <li className="flex items-center gap-3 text-dark-600"><FiCheckCircle className="text-secondary-500 text-xl" /> Reduced carbon footprint</li>
                <li className="flex items-center gap-3 text-dark-600"><FiCheckCircle className="text-secondary-500 text-xl" /> No operating costs</li>
                <li className="flex items-center gap-3 text-dark-600"><FiCheckCircle className="text-secondary-500 text-xl" /> 5-year warranty coverage</li>
                <li className="flex items-center gap-3 text-dark-600"><FiCheckCircle className="text-secondary-500 text-xl" /> Free installation and commissioning</li>
              </ul>
              <button onClick={openModal} className="btn btn-primary">
                Apply Now <FiArrowRight />
              </button>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600" 
                alt="PM KUSUM Scheme"
                className="w-full h-[300px] md:h-[500px] object-cover shadow-2xl rounded-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-20 bg-gradient-to-r from-secondary-600 to-secondary-700">
        <div className="container">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">Need Help Choosing the Right Product?</h2>
            <p className="text-base md:text-xl text-white/85 mb-6 md:mb-8">Our solar experts are here to help you find the perfect solution for your needs.</p>
            <button onClick={openModal} className="btn btn-primary">
              Talk to an Expert <FiArrowRight />
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Products;
